import {Injectable} from '@angular/core';
import {
    AdminPermissionsModel,
    GroupAdminsModel,
    GroupMemberModel,
    MessageModel,
    MessagesPaginationModel,
    MESSENGER_MAX_PAGES_COUNT,
    MessengerGroup
} from '@messenger/_models/messenger.models';
import {GroupAdministrativeData} from '@messenger/_store/messenger.reducer';
import {TranslateService} from "@ngx-translate/core";
import {OwnProfile} from '@common/models/user.models';
import {EntityState} from '@ngrx/entity';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {DahPaginationModel} from '@common/models/commonHTTP.models';

@Injectable()
export class MessengerHelperService {

    constructor(
        private translateService: TranslateService
    ) {
    }

    static sortGroupsComparer(a: MessengerGroup, b: MessengerGroup) {
        if (a?.lastMessage?.createTime && b?.lastMessage?.createTime) {
            if (a.lastMessage.createTime! < b.lastMessage.createTime!) {
                return 1;
            }

            if (a.lastMessage.createTime! > b.lastMessage.createTime!) {
                return -1;
            }

            return 0;
        } else {
            if (a.updated! < b.updated!) {
                return 1;
            }

            if (a.updated! > b.updated!) {
                return -1;
            }

            return 0;
        }
    }

    static setMessages(state: EntityState<MessageModel<any>>, newMessages: MessageModel<any>[], pagination: DahPaginationModel, direction?: 'prev' | 'next'): MessageModel<any>[] {
        const messages: Record<string, MessageModel<any>> = {};
        Object.values(state.entities).filter(msg => !!msg).forEach(msg => messages[msg.id] = msg);
        newMessages.forEach(msg => messages[msg.id] = msg);

        const list = Object.values(messages)
            .sort((a, b) => StoredComponentHelperService.sortArrayComparer(a, b, 'index', 'ASC'));

        if (pagination.size) {
            if (direction === 'next') {
                return list.slice(0, pagination.size * MESSENGER_MAX_PAGES_COUNT);
            }

            if (direction === 'prev') {
                return list.slice(-pagination.size * MESSENGER_MAX_PAGES_COUNT);
            }
        }

        return list;
    }

    static getPageDiff(response: MessageModel<any>[], items: MessageModel<any>[], direction: 'prev' | 'next' | undefined, pageSize: number): number {
        const storeMax = items[items.length - 1]!.index!;
        const storeMin = items[0]!.index!;
        const responseMin = response[response.length - 1].index!;

        if (direction == 'prev') {
            if (responseMin - storeMax >= 2) {
                const diff = responseMin - storeMax - 2;
                return (diff - diff % pageSize) / pageSize + 1;
            }
        } else if (direction == 'next') {
            if (responseMin - storeMin >= 0) {
                const diff = responseMin - storeMin;
                return (diff - diff % pageSize) / pageSize + 1;
            }
        }

        return 0;
    }

    static setPagination(responsePagination: DahPaginationModel, direction: 'prev' | 'next'): MessagesPaginationModel {
        if (direction == 'prev') {
            return {
                ...responsePagination,
                lastNumber: responsePagination.number! + MESSENGER_MAX_PAGES_COUNT - 1,
                firstNumber: responsePagination.number!,
                last: false,
                first: responsePagination.first
            };
        }
        return {
            ...responsePagination,
            lastNumber: responsePagination.number!,
            firstNumber: responsePagination.number! - MESSENGER_MAX_PAGES_COUNT + 1 > 0 ? responsePagination.number! - MESSENGER_MAX_PAGES_COUNT + 1 : 0,
            last: responsePagination.last,
            first: responsePagination.number! - MESSENGER_MAX_PAGES_COUNT + 1 <= 0
        };
    }

    parseMessagePayload(message: MessageModel<any>): MessageModel<any> {
        let result = message;

        if (message.type != 'TEXT' && message.type != 'POST' && message.payload && typeof message.payload == 'string') {
            result = {...message, payload: JSON.parse((message.payload as string))};
        }

        if (message.replyTo && message.replyTo.type != 'TEXT' && message.replyTo.type != 'POST' && message.replyTo.payload && typeof message.replyTo.payload == 'string') {
            result = {...message, replyTo: {...message.replyTo, payload: JSON.parse((message.replyTo.payload as string))}};
        }

        return result;
    }

    setGroupAdministrativeData(res: GroupAdminsModel, currentUser?: OwnProfile): GroupAdministrativeData {
        let admins: Record<string, GroupMemberModel> = {};
        let permissions: AdminPermissionsModel = {};
        if (currentUser?.id) {
            if (res?.mainAdmin) {
                admins = {...admins, [res.mainAdmin.userId!]: res.mainAdmin};

                if (res.mainAdmin.userId == currentUser.id && res.mainAdmin.adminPermissions) {
                    permissions = res.mainAdmin.adminPermissions;
                }
            }

            if (res?.admins?.length) {
                for (let i = 0; i < res.admins.length; i++) {
                    if (res.admins[i].userId == currentUser.id && res.admins[i].adminPermissions) {
                        permissions = res.admins[i].adminPermissions!;
                    }

                    admins = {...admins, [res.mainAdmin.userId!]: res.admins[i]};
                }
            }
        }

        return {admins, permissions, ...res?.mainAdmin && {mainAdminUserId: res.mainAdmin.userId}};
    }

    getMessagePreview(message: MessageModel<any>, currentUser?: OwnProfile): string {
        if (!message) {
            return '';
        }

        const prefix = message.groupType !== 'PERSONAL'
            ? (currentUser?.id === message.senderUserId
                ? this.translateService.instant('newTranslate.sections_Temp.messenger.you') + ': '
                : `${message.senderFirstName} ${message.senderLastName}: `)
            : '';

        switch (message.type) {
            case 'TEXT':
                if (message.status === 'REMOVED') {
                    return prefix + this.translateService.instant('newTranslate.sections_Temp.messenger.common.messageDeleted');
                } else {
                    const text = message.payload || '';
                    return prefix + (text.length > 35 ? text.substring(0, 35) + '...' : text);
                }
            case 'SYSTEM':
                if (message.payload?.type) {
                    return currentUser?.id === message.senderUserId
                        ? prefix + this.translateService.instant('newTranslate.sections_Temp.messenger.personalPayloadType.' + message.payload.type)
                        : prefix + this.translateService.instant('newTranslate.sections_Temp.messenger.payloadType.' + message.payload.type);
                }
                else {
                    return '';
                }
            case 'PHOTO':
                if (message.status === 'REMOVED') {
                    return prefix + this.translateService.instant('newTranslate.sections_Temp.messenger.common.messageDeleted');
                } else {
                    return prefix + '<span class="text-primary">' + this.translateService.instant('newTranslate.common_Temp.labels.image') + '</span>';
                }
            case 'FILE':
                if (message.status === 'REMOVED') {
                    return prefix + this.translateService.instant('newTranslate.sections_Temp.messenger.common.messageDeleted');
                } else {
                    return prefix + '<span class="text-primary">' + this.translateService.instant('newTranslate.sections_Temp.messenger.common.file') + '</span>';
                }
            default:
                return '';
        }
    }
}
